import {useRef, useState} from "react";
import {FieldArray, useField, useFormikContext} from "formik";
import {Dropzone, MIME_TYPES} from "@mantine/dropzone";
import {Group, Text, Modal} from "@mantine/core";
import {IconUpload, IconPhoto, IconX} from '@tabler/icons';
import DropzoneFile from "../DropzoneFile";
import PopOverInfo from "../PopOverInfo";
import samplePhotos from "../../assets/others/SamplePhotos.jpg"
import {saveFileContent} from "../Utils/saveFileContent";
import normalizeString from '../Utils/normalizeString';
import formatBytes from "../Utils/formatBtyes";

const modalStyles = {
    layout: {
        display: "flex",
        flexDirection: "column",
        justifyContent: "center",
        alignItems: "center"
    },
    title: {
        fontFamily: 'sans-serif',
        color: "#35303F",
        fontSize: "1rem",
        fontWeight: 600,
        textAlign: "center",
        lineHeight: "1.5"
    },
    text: {
        fontFamily: 'sans-serif',
        color: "#35303F",
        fontSize: "1rem",
        fontWeight: 500,
        textAlign: "start",
        lineHeight: "1",
        marginTop: "16px"
    },
    note: {
        fontFamily: 'sans-serif',
        color: "#9a9a9a",
        fontSize: "0.75rem",
        fontWeight: 400,
        textAlign: "center",
        lineHeight: "1",
        marginTop: "8px"
    }
}

const MAX_WEIGHT = 10485760;
const MAX_FILE_SIZE = 3 * 1024 ** 2;
const MAX_FILES = 10;

const DropzoneWrapper = (props) => {
    const {
        fieldName = "vehiculo.inspeccionPrevia.archivos",
        maxFiles = MAX_FILES,
        maxFileSize = MAX_FILE_SIZE,
        maxTotalSize = MAX_WEIGHT,
        supportedFileTypes = [MIME_TYPES.png, MIME_TYPES.jpeg, MIME_TYPES.pdf]
    } = props;
    const {setFieldValue} = useFormikContext();
    const currentWeight = useRef(0);
    const [error, setError] = useState(null)

    const [meta] = useField(fieldName);
    const {value} = meta;
    const [openPhotoHelp, setOpenPhotoHelp] = useState(false);

    return (
        <>
            <div className="structure__margin--topOnly">
                <div className="structure__start">
                    <label className="label">Archivos</label>
                    <div onClick={() => setOpenPhotoHelp(true)}>
                        <PopOverInfo descripcion="¿Qué archivos subir y cómo?"/></div>
                </div>
                <Dropzone
                    className={value.length >= maxFiles && "dropzone__disabled"}
                    onDrop={(files) => {
                        let json = []
                        let len = value.length
                        setError(null)
                        files.map(async function (file, index) {
                            const filename = file.name.split(/[.]+/)[0];
                            currentWeight.current += file.size;
                            if (len < 10 && currentWeight.current < maxTotalSize) {
                                let extension = file.name.split(/[.]+/).pop().toUpperCase();
                                if (extension !== 'JFIF') {
                                    if (filename.length === 0) {
                                        setError('El nombre del archivo debe tener al menos 1 caracter.')
                                        currentWeight.current -= file.size
                                    } else {
                                        const fileData = file.slice();
                                        const normalizedFilename = normalizeString(filename).substring(0, 24);
                                        const normalizedFilePath = `${normalizedFilename}.${extension}`;

                                        json.push({
                                            "name": normalizedFilePath,
                                            "extension": file.type.split("/")[1],
                                            "content": "",
                                            "file": new File([fileData], normalizedFilePath, {
                                                path: normalizedFilePath,
                                                size: fileData.size,
                                                type: file.type,
                                            }),
                                            "type": null,
                                        })

                                        saveFileContent(file).then((fileContent) => {
                                            json[index].content = fileContent;
                                        });
                                    }
                                } else {
                                    setError('El formato .JFIF no es válido.')
                                    currentWeight.current -= file.size
                                }
                            } else {
                                setError(`El tamaño total supera los ${formatBytes(maxTotalSize)}.`)
                                currentWeight.current -= file.size
                            }
                            len += 1
                        })
                        setFieldValue(fieldName, value.concat(json), false);
                    }}
                    onReject={(files) => console.log('rejected files', files)}
                    maxSize={maxFileSize}
                    maxFiles={maxFiles}
                    accept={supportedFileTypes}
                    disabled={value.length >= maxFiles}
                >
                    <Group position="center" spacing="xl" style={{minHeight: 220, pointerEvents: 'none'}}>
                        <Dropzone.Accept>
                            <IconUpload
                                size={50}
                                stroke={1.5}
                            />
                        </Dropzone.Accept>
                        <Dropzone.Reject>
                            <IconX
                                size={50}
                                stroke={1.5}
                            />
                        </Dropzone.Reject>
                        <Dropzone.Idle>
                            <IconPhoto size={50} stroke={1.5}/>
                        </Dropzone.Idle>

                        <div>
                            <Text size="xl" inline align="center">
                                Arrastre o haga clic para seleccionar los archivos
                            </Text>
                            <Text size="sm" color="dimmed" inline mt={7} align="center">
                                Puede adjuntar un máximo de {maxFiles} archivos (imágenes o pdf), cada archivo
                                no debe superar los {formatBytes(maxFileSize)} y en total no pueden
                                exceder los {formatBytes(maxTotalSize)}.
                            </Text>
                        </div>
                    </Group>
                </Dropzone>
                <div style={{
                    display: error ? 'flex' : 'none',
                    width: '100%',
                    paddingTop: '12px',
                    justifyContent: 'center'
                }}>
                    <p className="errorMessage structure__margin--topOnly"
                       style={{color: '#c92a2a', letterSpacing: -0.8}}
                    >
                        {error}
                    </p>
                </div>

                <FieldArray name={fieldName}>{({remove}) => (
                    <div className="dropzone__grid">
                        {
                            value.map((file, index) => {
                                return (
                                    <DropzoneFile
                                        file={file}
                                        index={index}
                                        onDelete={() => {
                                            remove(index)
                                            currentWeight.current -= file.file.size
                                        }}
                                    />)
                            })
                        }
                    </div>
                )}</FieldArray>
            </div>
            <Modal
                opened={openPhotoHelp}
                onClose={() => setOpenPhotoHelp(false)}
                centered
                withCloseButton={false}
                size="auto"
            >
                <div style={modalStyles['layout']}>
                    <p style={modalStyles['title']}>
                        Deben tomarse 4 (cuatro) fotos con luz diurna: desde una perspectiva frontal, otra posterior
                        y dos laterales.
                    </p>
                    <p style={modalStyles['text']}>Por ejemplo:</p>
                    <img src={samplePhotos} alt="" style={{maxWidth: '100%', maxHeight: '100%'}}/>
                    <p style={modalStyles['note']}>
                        NOTA: Si el auto es 0km puede adjuntar el certificado de no rodamiento en lugar de las
                        fotos.
                    </p>
                </div>
            </Modal>
        </>
    );
}

export {DropzoneWrapper};
